/**
 * OverlayContainer - Floating, draggable container for globe overlays.
 *
 * Provides a consistent container for overlay content with:
 * - Draggable positioning via drag handle
 * - Optional close button
 * - Backdrop blur and shadow styling
 */

import { cn } from '@/lib/style'
import { useOverlayPosition } from './useOverlayPosition'
import { X } from 'lucide-react'

interface OverlayContainerProps {
  /** Content to render inside the overlay */
  children: React.ReactNode
  /** Initial position of the overlay (default: { x: 20, y: 20 }) */
  initialPosition?: { x: number; y: number }
  /** Minimum width of the overlay */
  minWidth?: number
  /** Maximum width of the overlay */
  maxWidth?: number
  /** Called when close button is clicked (if not provided, no close button shown) */
  onClose?: () => void
  /** Additional class names for the container */
  className?: string
  /** Title shown in the drag handle area */
  title?: string
}

/**
 * Floating, draggable overlay container.
 *
 * @example
 * ```tsx
 * <OverlayContainer
 *   title="Your Trips"
 *   minWidth={350}
 *   onClose={() => setShowOverlay(false)}
 * >
 *   <div className="p-4">Content here</div>
 * </OverlayContainer>
 * ```
 */
export function OverlayContainer({
  children,
  initialPosition = { x: 20, y: 20 },
  minWidth = 320,
  maxWidth = 600,
  onClose,
  className,
  title,
}: OverlayContainerProps) {
  const { position, isDragging, handlers } = useOverlayPosition(initialPosition)

  return (
    <div
      className={cn(
        'absolute bg-white/95 rounded-md shadow-2xl backdrop-blur-sm',
        'border border-gray-200/50',
        isDragging && 'select-none cursor-grabbing',
        className
      )}
      style={{
        left: position.x,
        top: position.y,
        minWidth,
        maxWidth,
        zIndex: 50,
      }}
    >
      {/* Drag handle */}
      <div
        className={cn(
          'h-10 flex items-center justify-between px-3 border-b border-gray-100',
          'cursor-grab',
          isDragging && 'cursor-grabbing'
        )}
        {...handlers}
      >
        {title ? (
          <h2 className="font-['Pioneer_Boulevard'] text-base text-gray-900 select-none">
            {title}
          </h2>
        ) : (
          <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto" />
        )}
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Content */}
      {children}
    </div>
  )
}
